import { photoDimensions, PRIVATE_PICTURE_ACCEPT } from './62-private-raster.mjs';
export { photoDimensions } from './62-private-raster.mjs';

// Closed public artwork list: these accepted originals ship with the application.
export const PHOTO_SAND_DEMOS = Object.freeze([
  Object.freeze({
    id: 'garden-dog',
    label: 'Garden dog',
    url: new URL(
      '../assets/lifestyle-amusing-animals-001-the-opening-bow-640.webp',
      import.meta.url,
    ).href,
  }),
  Object.freeze({
    id: 'cushion-dog',
    label: 'Cushion dog',
    url: new URL(
      '../assets/lifestyle-amusing-animals-003-the-cushion-committee-640.webp',
      import.meta.url,
    ).href,
  }),
  Object.freeze({
    id: 'breezy-ears',
    label: 'Breezy ears',
    url: new URL(
      '../assets/lifestyle-amusing-animals-004-ears-on-the-breeze-640.webp',
      import.meta.url,
    ).href,
  }),
  Object.freeze({
    id: 'banff-lake',
    label: 'Banff lake',
    url: new URL('../assets/travel-100-banff-640.webp', import.meta.url).href,
  }),
  Object.freeze({
    id: 'lisbon-light',
    label: 'Lisbon light',
    url: new URL('../assets/travel-100-lisbon-640.webp', import.meta.url).href,
  }),
  Object.freeze({
    id: 'amsterdam-canal',
    label: 'Amsterdam canal',
    url: new URL('../assets/travel-100-amsterdam-640.webp', import.meta.url).href,
  }),
]);

/** Resolve only a shipped demo identifier. Arbitrary URLs are never accepted. */
export function photoSandDemo(id) {
  const demo = PHOTO_SAND_DEMOS.find((item) => item.id === id);
  if (!demo) throw new Error('Choose one of the original demo scenes.');
  return demo;
}

/** Deterministic weighted median-cut colour reduction, bounded to twelve local pigments. */
export function quantizePhoto(rgba, width, height) {
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    height < 1 ||
    width > 128 ||
    height > 192 ||
    rgba.length !== width * height * 4
  )
    throw new Error('Photo canvas exceeds its bounds.');
  const histogram = new Map();
  for (let i = 0; i < rgba.length; i += 4) {
    const alpha = rgba[i + 3] / 255;
    const rgb = [0, 1, 2].map((channel) =>
      Math.round(rgba[i + channel] * alpha + 255 * (1 - alpha)),
    );
    const key = (rgb[0] >> 3) * 1024 + (rgb[1] >> 3) * 32 + (rgb[2] >> 3);
    const bin = histogram.get(key) ?? { rgb: [0, 0, 0], count: 0, key };
    rgb.forEach((value, channel) => {
      bin.rgb[channel] += value;
    });
    bin.count++;
    histogram.set(key, bin);
  }
  const bins = [...histogram.values()].map((bin) => ({
    ...bin,
    rgb: bin.rgb.map((value) => value / bin.count),
  }));
  const boxes = [bins];
  const spread = (box) =>
    [0, 1, 2].map(
      (channel) =>
        Math.max(...box.map((bin) => bin.rgb[channel])) -
        Math.min(...box.map((bin) => bin.rgb[channel])),
    );
  while (boxes.length < 12) {
    let selected = -1,
      best = 0,
      channel = 0;
    boxes.forEach((box, index) => {
      const ranges = spread(box);
      const range = Math.max(...ranges);
      const weight = box.reduce((sum, bin) => sum + bin.count, 0);
      if (box.length > 1 && range * weight > best) {
        selected = index;
        best = range * weight;
        channel = ranges.indexOf(range);
      }
    });
    if (selected < 0) break;
    const box = boxes[selected].sort((a, b) => a.rgb[channel] - b.rgb[channel] || a.key - b.key);
    const total = box.reduce((sum, bin) => sum + bin.count, 0);
    let split = 1,
      count = box[0].count;
    while (split < box.length - 1 && count < total / 2) count += box[split++].count;
    boxes.splice(selected, 1, box.slice(0, split), box.slice(split));
  }
  const mapping = new Map();
  const palette = boxes.map((box, index) => {
    const count = box.reduce((sum, bin) => sum + bin.count, 0);
    box.forEach((bin) => mapping.set(bin.key, index));
    return {
      rgb: [0, 1, 2].map((channel) =>
        Math.round(box.reduce((sum, bin) => sum + bin.rgb[channel] * bin.count, 0) / count),
      ),
      count,
    };
  });
  const pixels = new Uint8Array(width * height);
  for (let i = 0; i < pixels.length; i++) {
    const alpha = rgba[i * 4 + 3] / 255;
    const rgb = [0, 1, 2].map(
      (channel) => Math.round(rgba[i * 4 + channel] * alpha + 255 * (1 - alpha)) >> 3,
    );
    pixels[i] = mapping.get(rgb[0] * 1024 + rgb[1] * 32 + rgb[2]);
  }
  return { width, height, pixels, palette };
}

/** Exact extraction ledger: every source pixel is either pinned or in its original-column tray. */
export function createSandLedger(photo) {
  const order = [];
  let released = new Uint8Array(photo.pixels.length);
  function release(colour) {
    if (
      !Number.isInteger(colour) ||
      colour < 0 ||
      colour >= photo.palette.length ||
      order.includes(colour)
    )
      return [];
    order.push(colour);
    const grains = [];
    photo.pixels.forEach((value, index) => {
      if (value === colour) {
        released[index] = 1;
        grains.push(index);
      }
    });
    return grains;
  }
  return {
    release,
    undo() {
      if (!order.length) return false;
      order.pop();
      released = Uint8Array.from(photo.pixels, (colour) => Number(order.includes(colour)));
      return true;
    },
    reset() {
      order.length = 0;
      released.fill(0);
    },
    released: () => released,
    order: () => [...order],
    complete: () => released.every((value) => value === 1),
  };
}

/** Private, unscored photo surface. No photo bytes enter canonical game state or any network API. */
export function createPhotoSands(
  ctx,
  playfield,
  board,
  { visible = () => true, readState = () => null } = {},
) {
  const doc = board.ownerDocument ?? globalThis.document;
  const win = doc?.defaultView ?? globalThis;
  const e = ctx.e;
  const originalInert = board.inert === true;
  const button = e('button', 'game-photo-entry', 'Photo sands');
  button.type = 'button';
  button.hidden = true;
  const surface = e('section', 'game-photo-sands');
  surface.hidden = true;
  surface.setAttribute('aria-label', 'Photo sands, unscored');
  const head = e('div', 'game-photo-head');
  const label = e('strong', '', 'Photo sands · unscored');
  const back = e('button', '', 'Back');
  back.type = 'button';
  const choose = e('button', '', 'Photo');
  choose.setAttribute('aria-label', 'Choose a private photo from your device');
  choose.type = 'button';
  const input = e('input', '');
  input.type = 'file';
  input.accept = PRIVATE_PICTURE_ACCEPT;
  input.hidden = true;
  const canvas = e('canvas', 'game-photo-canvas');
  canvas.setAttribute('aria-label', 'Photo grains and collection tray');
  const status = e(
    'p',
    'game-photo-status',
    'Try an original scene, or choose a private photo. Your photo stays on this device.',
  );
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const palette = e('div', 'game-photo-palette');
  palette.hidden = true;
  palette.setAttribute('aria-label', 'Release a pigment');
  const tools = e('div', 'game-photo-tools');
  const undo = e('button', '', 'Undo');
  undo.type = 'button';
  const reset = e('button', '', 'Reset');
  reset.type = 'button';
  head.append(label, back);
  const demos = e('button', '', 'Demos');
  demos.type = 'button';
  const gallery = e('div', 'game-photo-gallery');
  gallery.setAttribute('aria-label', 'Original demo scenes');
  tools.append(choose, undo, reset, demos);
  const completion = e('section', 'game-photo-complete');
  completion.hidden = true;
  completion.setAttribute('aria-label', 'Scene transformed');
  const nextScene = e('button', '', 'Next scene');
  nextScene.type = 'button';
  completion.append(
    e('strong', '', 'Scene transformed'),
    e('span', '', 'Every pigment gathered. Keep a moment, or try another scene.'),
    nextScene,
  );
  surface.append(head, canvas, status, palette, tools, gallery, completion, input);
  const host = playfield.parentElement ?? playfield;
  host.append(surface);
  const inertSiblings = new Map();
  let owningDialog;
  function dialogClosed() {
    surface.hidden = true;
    button.hidden = true;
    restoreUnderlying();
    stop();
  }
  function bindDialog() {
    const dialog = host.closest?.('dialog');
    if (dialog === owningDialog) return;
    owningDialog?.removeEventListener('close', dialogClosed);
    owningDialog = dialog;
    owningDialog?.addEventListener('close', dialogClosed);
  }
  function restoreUnderlying() {
    for (const [node, inert] of inertSiblings) node.inert = inert;
    inertSiblings.clear();
    board.inert = originalInert;
  }
  let source = 'private',
    demoRequest,
    finishAnimation,
    completedShown = false;
  let photo,
    ledger,
    paint,
    disposed = false,
    generation = 0,
    frame = 0,
    lastTime = 0;
  let pending = [],
    active = [],
    columns = [],
    settled = new Set();
  const listeners = [];
  const pigmentListeners = [];
  const on = (node, name, fn) => {
    node.addEventListener(name, fn);
    listeners.push(() => node.removeEventListener(name, fn));
  };
  const reduced = () => win.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  const enabled = () =>
    !disposed && !surface.hidden && board.dataset.immersive === 'true' && visible() && !doc?.hidden;
  function stop() {
    finishAnimation?.cancel();
    finishAnimation = null;
    if (frame) win.cancelAnimationFrame?.(frame);
    frame = 0;
    lastTime = 0;
  }
  function settle(index) {
    if (settled.has(index)) return;
    settled.add(index);
    columns[index % photo.width].push(photo.pixels[index]);
  }
  function redraw() {
    if (!photo || !paint) return;
    const { width, height, pixels } = photo;
    paint.fillStyle = '#101d32';
    paint.fillRect(0, 0, width, height * 2 + 8);
    pixels.forEach((colour, index) => {
      if (ledger.released()[index]) return;
      paint.fillStyle = `rgb(${photo.palette[colour].rgb.join(',')})`;
      paint.fillRect(index % width, Math.floor(index / width), 1, 1);
    });
    columns.forEach((stack, x) =>
      stack.forEach((colour, y) => {
        paint.fillStyle = `rgb(${photo.palette[colour].rgb.join(',')})`;
        paint.fillRect(x, height * 2 + 7 - y, 1, 1);
      }),
    );
    active.forEach((grain) => {
      paint.fillStyle = `rgb(${photo.palette[pixels[grain.index]].rgb.join(',')})`;
      paint.fillRect(grain.index % width, grain.y, 1, 1);
    });
    paint.fillStyle = '#ffffff55';
    paint.fillRect(0, height + 4, width, 1);
  }
  function clearCompletion() {
    completion.hidden = true;
    completedShown = false;
    finishAnimation?.cancel();
    finishAnimation = null;
  }
  function showGallery() {
    completion.hidden = true;
    gallery.hidden = false;
    finishAnimation?.cancel();
    finishAnimation = null;
    gallery.children[0]?.focus({ preventScroll: true });
  }
  function announce() {
    const complete = ledger.complete() && !pending.length && !active.length;
    if (!complete) clearCompletion();
    else if (!completedShown) {
      completedShown = true;
      completion.hidden = false;
      if (!reduced() && enabled() && typeof completion.animate === 'function') {
        finishAnimation = completion.animate(
          [
            { opacity: 0, filter: 'blur(4px)' },
            { opacity: 1, filter: 'blur(0)' },
          ],
          { duration: 500, easing: 'ease-out' },
        );
        finishAnimation.finished?.catch(() => {});
      }
    }
    const count = ledger.released().reduce((sum, value) => sum + value, 0);
    status.textContent =
      ledger.complete() && !pending.length && !active.length
        ? 'Every pigment collected. Your scene, a new landscape.'
        : `${count.toLocaleString()} of ${photo.pixels.length.toLocaleString()} grains released · ${source === 'private' ? 'private' : 'demo artwork'} and unscored`;
    undo.disabled = !ledger.order().length;
    reset.disabled = !ledger.order().length;
    [...palette.children].forEach((chip, index) => {
      chip.disabled = ledger.order().includes(index);
    });
  }
  function tick(time) {
    frame = 0;
    if (!enabled()) return;
    const dt = Math.min(0.032, lastTime ? (time - lastTime) / 1000 : 1 / 60);
    lastTime = time;
    const spawning = Math.min(128, 512 - active.length, pending.length);
    for (let i = 0; i < spawning; i++) {
      const index = pending.pop();
      active.push({ index, y: Math.floor(index / photo.width), speed: 0 });
    }
    active = active.filter((grain) => {
      grain.speed += 180 * dt;
      grain.y += grain.speed * dt;
      const floor = photo.height * 2 + 7 - columns[grain.index % photo.width].length;
      if (grain.y >= floor) {
        settle(grain.index);
        return false;
      }
      return true;
    });
    redraw();
    if (pending.length || active.length) frame = win.requestAnimationFrame(tick);
    else {
      lastTime = 0;
      announce();
    }
  }
  function start() {
    if (!enabled() || !photo || frame) return;
    if (reduced() || typeof win.requestAnimationFrame !== 'function') {
      [...pending, ...active.map((grain) => grain.index)].forEach(settle);
      pending = [];
      active = [];
      redraw();
      announce();
    } else if (pending.length || active.length) frame = win.requestAnimationFrame(tick);
  }
  function rebuild() {
    stop();
    pending = [];
    active = [];
    settled = new Set();
    columns = Array.from({ length: photo.width }, () => []);
    ledger.released().forEach((value, index) => {
      if (value) settle(index);
    });
    redraw();
    announce();
  }
  async function load(file, provenance = 'private', own = ++generation) {
    stop();
    status.textContent = `Making your ${provenance === 'private' ? 'private photo' : 'demo'} palette…`;
    choose.disabled = true;
    let bitmap;
    try {
      const size = await photoDimensions(file);
      if (disposed || own !== generation) return;
      if (typeof win.createImageBitmap !== 'function')
        throw new Error(
          'Photo sands needs raster decoding support. Your original Sand Layers remains available.',
        );
      bitmap = await win.createImageBitmap(file, {
        resizeWidth: size.width,
        resizeHeight: size.height,
        resizeQuality: 'high',
        imageOrientation: 'from-image',
      });
      if (disposed || own !== generation) return;
      if (bitmap.width !== size.width || bitmap.height !== size.height)
        throw new Error('This photo could not be resized safely. Try a JPEG or PNG.');
      const stage = doc.createElement('canvas');
      stage.width = size.width;
      stage.height = size.height;
      const drawing = stage.getContext('2d', { willReadFrequently: true });
      if (!drawing) throw new Error('Photo sands is unavailable in this browser.');
      drawing.drawImage(bitmap, 0, 0, size.width, size.height);
      photo = quantizePhoto(
        drawing.getImageData(0, 0, size.width, size.height).data,
        size.width,
        size.height,
      );
      clearCompletion();
      source = provenance;
      label.textContent =
        source === 'private' ? 'Private photo · unscored' : 'Original demo · unscored';
      gallery.hidden = true;
      ledger = createSandLedger(photo);
      paint = canvas.getContext('2d');
      if (!paint) throw new Error('Photo sands is unavailable in this browser.');
      canvas.width = size.width;
      canvas.height = size.height * 2 + 8;
      pigmentListeners.splice(0).forEach((remove) => remove());
      palette.replaceChildren();
      palette.hidden = false;
      photo.palette.forEach((pigment, index) => {
        const chip = e('button', 'game-photo-pigment', String(index + 1));
        chip.type = 'button';
        chip.style.setProperty('--pigment', `rgb(${pigment.rgb.join(',')})`);
        chip.setAttribute('aria-label', `Release pigment ${index + 1}, ${pigment.count} grains`);
        const release = () => {
          pending.push(...ledger.release(index));
          announce();
          start();
        };
        chip.addEventListener('click', release);
        pigmentListeners.push(() => chip.removeEventListener('click', release));
        palette.append(chip);
      });
      rebuild();
      palette.children[0]?.focus({ preventScroll: true });
    } catch (error) {
      if (!disposed && own === generation)
        status.textContent = error instanceof Error ? error.message : 'Unable to read this photo.';
    } finally {
      bitmap?.close();
      if (own === generation && !disposed) {
        choose.disabled = false;
        input.value = '';
      }
    }
  }
  on(button, 'click', () => {
    if (board.dataset.immersive !== 'true') return;
    bindDialog();
    surface.hidden = false;
    const more = host.querySelector?.('.game-more');
    if (more) more.open = false;
    for (const node of host.children) {
      if (node === surface || node.classList?.contains('game-heading')) continue;
      if (!inertSiblings.has(node)) inertSiblings.set(node, node.inert === true);
      node.inert = true;
    }
    board.inert = true;
    choose.focus({ preventScroll: true });
    start();
  });
  on(back, 'click', () => {
    stop();
    surface.hidden = true;
    restoreUnderlying();
    (host.querySelector?.('.game-more summary') ?? button).focus({ preventScroll: true });
  });
  on(choose, 'click', () => input.click());
  on(demos, 'click', showGallery);
  on(nextScene, 'click', showGallery);
  PHOTO_SAND_DEMOS.forEach((demo) => {
    const item = e('button', 'game-photo-demo');
    item.type = 'button';
    item.setAttribute('aria-label', `Try ${demo.label}`);
    const image = e('img', '');
    image.src = demo.url;
    image.alt = '';
    image.loading = 'lazy';
    item.append(image, e('span', '', demo.label));
    gallery.append(item);
    on(item, 'click', async () => {
      const own = ++generation;
      demoRequest?.abort();
      demoRequest = new AbortController();
      stop();
      choose.disabled = true;
      status.textContent = `Opening ${demo.label}, original public artwork…`;
      try {
        const selected = photoSandDemo(demo.id);
        const response = await win.fetch(selected.url, {
          credentials: 'omit',
          mode: 'same-origin',
          redirect: 'error',
          signal: demoRequest.signal,
        });
        if (!response.ok)
          throw new Error('That demo could not load. Try another scene or your own photo.');
        const file = await response.blob();
        if (disposed || own !== generation) return;
        await load(file, 'demo', own);
      } catch (error) {
        if (!disposed && own === generation)
          status.textContent = error instanceof Error ? error.message : 'Unable to load that demo.';
      } finally {
        if (!disposed && own === generation) choose.disabled = false;
      }
    });
  });
  on(input, 'change', () => {
    const file = input.files?.[0];
    if (file) void load(file);
  });
  on(undo, 'click', () => {
    if (ledger?.undo()) rebuild();
  });
  on(reset, 'click', () => {
    if (ledger) {
      ledger.reset();
      rebuild();
    }
  });
  undo.disabled = true;
  reset.disabled = true;
  return {
    button,
    update() {
      button.hidden = board.dataset.immersive !== 'true' || readState()?.gameId !== 'sand-layers';
      if (button.hidden) {
        surface.hidden = true;
        restoreUnderlying();
      }
      if (enabled()) start();
      else stop();
    },
    cancel: stop,
    dispose() {
      disposed = true;
      demoRequest?.abort();
      owningDialog?.removeEventListener('close', dialogClosed);
      owningDialog = null;
      restoreUnderlying();
      generation++;
      stop();
      listeners.splice(0).forEach((remove) => remove());
      pigmentListeners.splice(0).forEach((remove) => remove());
      photo = ledger = paint = null;
      pending = [];
      active = [];
      columns = [];
      settled.clear();
      surface.remove();
      button.remove();
    },
  };
}
